import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  BarChart3, Users, CheckCircle, TrendingDown,
  Target, AlertTriangle, LogOut, RefreshCw,
  ArrowRight, Zap, PieChart, Calendar, FlaskConical
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminDashboard = () => {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState(30);
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [answers, setAnswers] = useState(null);
  const [actionSummary, setActionSummary] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch each endpoint independently so one failure doesn't block others
      const safeFetch = async (url) => {
        try {
          const res = await fetch(url, { headers });
          if (res.ok) return await res.json();
          return null;
        } catch { return null; }
      };

      const [overviewData, funnelData, answersData, actionData] = await Promise.all([
        safeFetch(`${API_URL}/api/analytics/overview?days=${dateRange}`),
        safeFetch(`${API_URL}/api/analytics/funnel?days=${dateRange}`),
        safeFetch(`${API_URL}/api/analytics/answers?days=${dateRange}`),
        safeFetch(`${API_URL}/api/analytics/actionable-summary?days=${dateRange}`)
      ]);

      if (overviewData) setOverview(overviewData);
      if (funnelData) setFunnel(funnelData);
      if (answersData) setAnswers(answersData);
      if (actionData) setActionSummary(actionData);
      
      if (!overviewData && !funnelData) {
        setError('Failed to load dashboard data');
      } else {
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateRange, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0f' }}>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full animate-pulse" style={{ background: 'linear-gradient(135deg, #7874F8 0%, #09C9E9 100%)' }} />
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0f' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 border-b" style={{ background: '#0f0f17', borderColor: '#1e1e2e' }}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-serif gradient-text" data-testid="dashboard-brand">Hashi-aid</h1>
              <span className="px-3 py-1 rounded-full text-xs font-medium" data-testid="dashboard-badge" style={{ background: 'rgba(120, 116, 248, 0.15)', color: '#a5a2ff' }}>
                Quiz Cockpit
              </span>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="relative">
                <select
                  data-testid="date-range-selector"
                  value={dateRange}
                  onChange={(e) => setDateRange(Number(e.target.value))}
                  className="appearance-none rounded-lg px-4 py-2 pr-10 text-sm text-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  style={{ background: '#1a1a26', border: '1px solid #2a2a3a' }}
                >
                  <option value={7}>Last 7 days</option>
                  <option value={14}>Last 14 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                </select>
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
              </div>
              
              <Link
                to="/admin/split-tests"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-gray-300 transition hover:bg-white/5 text-sm font-medium"
                style={{ border: '1px solid #2a2a3a' }}
                data-testid="split-tests-link"
              >
                <FlaskConical className="w-4 h-4 text-purple-400" /> Split Tests
              </Link>
              
              <button
                data-testid="refresh-button"
                onClick={() => fetchData(true)}
                disabled={refreshing}
                className="p-2 rounded-lg transition hover:bg-white/5"
                style={{ border: '1px solid #2a2a3a' }}
              >
                <RefreshCw className={`w-5 h-5 text-gray-400 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              
              <button
                data-testid="logout-button"
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-gray-400 transition hover:bg-white/5"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main data-testid="dashboard-main" className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div data-testid="dashboard-error" className="mb-6 p-4 rounded-lg" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#f87171' }}>
            {error}
          </div>
        )}

        {/* Key Metrics */}
        <div data-testid="metrics-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <MetricCard title="Quiz Starts" value={overview?.total_sessions || 0} icon={Users} color="#7874F8" subtitle={`Last ${dateRange} days`} />
          <MetricCard title="Completions" value={overview?.completed_sessions || 0} icon={CheckCircle} color="#09C9E9" subtitle={`${overview?.completion_rate || 0}% conversion`} />
          <MetricCard title="Drop-offs" value={(overview?.total_sessions || 0) - (overview?.completed_sessions || 0)} icon={TrendingDown} color="#F59E0B" subtitle="Users who didn't complete" />
          <MetricCard title="Conversion Rate" value={`${overview?.completion_rate || 0}%`} icon={Target} color={overview?.completion_rate >= 40 ? '#10B981' : '#EF4444'} subtitle={overview?.completion_rate >= 40 ? 'Performing well' : 'Needs improvement'} />
        </div>

        {/* Priority Actions */}
        {actionSummary?.priority_actions?.length > 0 && (
          <div className="mb-8">
            <h2 className="text-base font-semibold text-gray-200 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              Priority Actions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {actionSummary.priority_actions.map((action, index) => (
                <ActionCard key={index} action={action} />
              ))}
            </div>
          </div>
        )}

        {/* Funnel Analysis */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
          <div className="lg:col-span-2 rounded-xl p-6" data-testid="funnel-chart-section" style={{ background: '#13131a', border: '1px solid #1e1e2e' }}>
            <h2 className="text-base font-semibold text-gray-200 mb-6 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-400" />
              Step-by-Step Funnel
            </h2>
            <FunnelChart data={funnel?.funnel || []} onStepAction={(action, step) => {
              if (action === 'new-test') navigate(`/admin/split-tests/new?step=${step.step_number}`);
            }} />
          </div>
          
          <div className="rounded-xl p-6" data-testid="dropoff-analysis-section" style={{ background: '#13131a', border: '1px solid #1e1e2e' }}>
            <h2 className="text-base font-semibold text-gray-200 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              Drop-off Analysis
            </h2>
            <DropoffList data={funnel?.funnel || []} />
          </div>
        </div>

        {/* Insights */}
        {funnel?.insights?.length > 0 && (
          <div className="mb-8">
            <h2 className="text-base font-semibold text-gray-200 mb-4">Insights</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {funnel.insights.map((insight, index) => (
                <InsightCard key={index} insight={insight} />
              ))}
            </div>
          </div>
        )}

        {/* Answer Distribution */}
        {answers?.answers_by_step && Object.keys(answers.answers_by_step).length > 0 && (
          <div className="rounded-xl p-6" data-testid="answer-distribution-section" style={{ background: '#13131a', border: '1px solid #1e1e2e' }}>
            <h2 className="text-base font-semibold text-gray-200 mb-6 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-cyan-400" />
              Answer Distribution by Step
            </h2>
            <AnswerDistribution data={answers.answers_by_step} />
          </div>
        )}
      </main>
    </div>
  );
};

// Component: Metric Card
const MetricCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <div data-testid={`metric-card-${title.toLowerCase().replace(/\s+/g, '-')}`} className="rounded-xl p-5 transition hover:translate-y-[-2px]" style={{ background: '#13131a', border: '1px solid #1e1e2e' }}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{title}</p>
        <p className="text-3xl font-bold" style={{ color }}>{value}</p>
        <p className="text-xs text-gray-600 mt-1">{subtitle}</p>
      </div>
      <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
    </div>
  </div>
);

// Component: Action Card
const ActionCard = ({ action }) => {
  const priorityColors = {
    1: { bg: 'rgba(239, 68, 68, 0.08)', border: 'rgba(239, 68, 68, 0.3)', text: '#fca5a5', accent: '#ef4444' },
    2: { bg: 'rgba(245, 158, 11, 0.08)', border: 'rgba(245, 158, 11, 0.3)', text: '#fcd34d', accent: '#f59e0b' },
    3: { bg: 'rgba(59, 130, 246, 0.08)', border: 'rgba(59, 130, 246, 0.3)', text: '#93c5fd', accent: '#3b82f6' }
  };
  const colors = priorityColors[action.priority] || priorityColors[3];

  return (
    <div className="rounded-xl p-4" style={{ background: colors.bg, border: `1px solid ${colors.border}` }}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-bold px-2 py-1 rounded" style={{ backgroundColor: colors.accent, color: '#fff' }}>
          P{action.priority}
        </span>
        <span className="text-xs font-medium" style={{ color: colors.text }}>{action.category}</span>
      </div>
      <h3 className="font-semibold mb-1 text-sm" style={{ color: colors.text }}>{action.action}</h3>
      <p className="text-sm mb-2 text-gray-500">{action.details}</p>
      <p className="text-xs font-medium" style={{ color: colors.text }}>
        Impact: {action.impact}
      </p>
    </div>
  );
};

// Component: Funnel Chart
const FunnelChart = ({ data, onStepAction }) => {
  const maxViews = Math.max(...data.map(d => d.views), 1);
  const [menuStep, setMenuStep] = useState(null);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });

  const handleBarClick = (e, step) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({ x: rect.right - 180, y: rect.bottom + 4 });
    setMenuStep(menuStep === step.step_number ? null : step.step_number);
  };

  // Close menu on outside click
  React.useEffect(() => {
    const close = () => setMenuStep(null);
    if (menuStep) document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuStep]);

  return (
    <div className="space-y-3 relative">
      {data.map((step, index) => {
        const width = (step.views / maxViews) * 100;
        const isWorst = step.drop_off_rate > 20;
        
        return (
          <div key={step.step_number} className="flex items-center gap-4 relative">
            <div className="w-8 text-sm font-medium text-gray-600">{step.step_number}</div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-400 truncate">{step.step_name}</span>
                <span className="text-sm font-medium text-gray-300">{step.views}</span>
              </div>
              <div
                className="h-7 rounded-lg overflow-hidden relative cursor-pointer group"
                style={{ background: '#1a1a26' }}
                onClick={(e) => handleBarClick(e, step)}
                data-testid={`funnel-bar-${step.step_number}`}
              >
                <div
                  className="h-full rounded-lg transition-all duration-500 group-hover:brightness-125"
                  style={{
                    width: `${width}%`,
                    background: isWorst
                      ? 'linear-gradient(90deg, #EF4444, #F87171)'
                      : 'linear-gradient(90deg, #7874F8, #09C9E9)'
                  }}
                />
                {index > 0 && step.drop_off_rate > 0 && (
                  <span className={`absolute right-2 top-1/2 transform -translate-y-1/2 text-xs font-medium ${isWorst ? 'text-red-400' : 'text-gray-500'}`}>
                    -{step.drop_off_rate}%
                  </span>
                )}
              </div>

              {/* Popup Menu */}
              {menuStep === step.step_number && (
                <div
                  className="absolute right-0 z-50 mt-1 rounded-lg shadow-xl py-1 w-48"
                  style={{ background: '#1e1e2e', border: '1px solid #2a2a3a' }}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`step-menu-${step.step_number}`}
                >
                  <button
                    data-testid={`new-test-step-${step.step_number}`}
                    onClick={() => {
                      setMenuStep(null);
                      if (onStepAction) onStepAction('new-test', step);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white flex items-center gap-2 transition"
                    style={{ background: 'transparent' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#7874F815'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <FlaskConical className="w-4 h-4 text-purple-400" />
                    New Split Test
                  </button>
                  <button
                    onClick={() => setMenuStep(null)}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-500 hover:text-gray-300 flex items-center gap-2 transition"
                    style={{ background: 'transparent' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#ffffff08'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <BarChart3 className="w-4 h-4" />
                    View Details
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Component: Dropoff List
const DropoffList = ({ data }) => {
  const sortedByDropoff = [...data].filter(d => d.drop_off_rate > 0).sort((a, b) => b.drop_off_rate - a.drop_off_rate);
  
  return (
    <div className="space-y-3">
      {sortedByDropoff.slice(0, 5).map((step, index) => (
        <div key={step.step_number} className="flex items-center justify-between p-3 rounded-lg" style={{ background: '#1a1a26' }}>
          <div className="flex items-center gap-3">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
              index === 0 ? 'bg-red-500 text-white' : 'text-gray-400'
            }`} style={index !== 0 ? { background: '#2a2a3a' } : {}}>
              {step.step_number}
            </span>
            <div>
              <p className="text-sm font-medium text-gray-300">{step.step_name}</p>
              <p className="text-xs text-gray-600">{step.drop_off_count} users lost</p>
            </div>
          </div>
          <span className={`text-sm font-bold ${step.drop_off_rate > 20 ? 'text-red-400' : 'text-orange-400'}`}>
            -{step.drop_off_rate}%
          </span>
        </div>
      ))}
      {sortedByDropoff.length === 0 && (
        <p className="text-sm text-gray-600 text-center py-4">No drop-off data yet</p>
      )}
    </div>
  );
};

// Component: Insight Card
const InsightCard = ({ insight }) => {
  const typeStyles = {
    critical: { bg: 'rgba(239, 68, 68, 0.08)', border: '#ef4444', text: '#fca5a5' },
    warning: { bg: 'rgba(245, 158, 11, 0.08)', border: '#f59e0b', text: '#fcd34d' },
    success: { bg: 'rgba(16, 185, 129, 0.08)', border: '#10b981', text: '#6ee7b7' },
    info: { bg: 'rgba(59, 130, 246, 0.08)', border: '#3b82f6', text: '#93c5fd' }
  };
  const style = typeStyles[insight.type] || typeStyles.info;

  return (
    <div className="rounded-xl border-l-4 p-4" style={{ backgroundColor: style.bg, borderColor: style.border }}>
      <div className="flex items-start gap-3">
        <span className="text-lg">{insight.icon}</span>
        <div>
          <h3 className="font-semibold mb-1" style={{ color: style.text }}>{insight.title}</h3>
          <p className="text-sm text-gray-500 mb-2">{insight.message}</p>
          <p className="text-sm font-medium text-gray-400">
            <ArrowRight className="w-4 h-4 inline mr-1" />
            {insight.action}
          </p>
        </div>
      </div>
    </div>
  );
};

// Component: Answer Distribution
const AnswerDistribution = ({ data }) => {
  const steps = Object.entries(data).slice(0, 6);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {steps.map(([stepNum, stepData]) => (
        <div key={stepNum} className="p-4 rounded-lg" style={{ background: '#1a1a26' }}>
          <h4 className="font-medium text-gray-300 mb-3 text-sm">Step {stepNum}: {stepData.step_name}</h4>
          <div className="space-y-2">
            {stepData.answers.slice(0, 4).map((answer, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <span className="text-sm text-gray-500 truncate flex-1 mr-2">{answer.value}</span>
                <span className="text-sm font-medium" style={{ color: '#a5a2ff' }}>{answer.percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdminDashboard;
